/**
 * A-06 — status master list + inline label edit (code immutable).
 *
 * A table (components/ui/VirtualTable) with search (code, label or description), type and stage
 * filters, sort (+ direction) and a batch size for the pages loaded as you scroll. The API returns
 * the whole master, so these run in the browser (lib/clientList.ts).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { listStatuses, updateStatus } from "../../api/admin";
import type { Status } from "../../api/admin";
import { describeError } from "../../api/errors";
import { ExportButton } from "../../components/ui/ExportButton";
import {
  BatchSizeFilter,
  ClearFilters,
  FilterBar,
  SearchBox,
  SelectFilter,
  SortFilter,
} from "../../components/ui/FilterBar";
import { EditIcon, IconButton, RowActions, SaveIcon } from "../../components/ui/IconButton";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { toastFailure } from "../../lib/adminFeedback";
import { narrowAndSort, useClientPages } from "../../lib/clientList";
import type { SortValue } from "../../lib/clientList";
import type { TableColumn } from "../../lib/tableColumns";
import type { ExportColumn } from "../../lib/tableExport";

// How a status is reached (the API's transition types); a type not named here shows as it comes.
const TYPE_LABEL: Record<string, string> = {
  manual: "Manual",
  "auto.payment": "Auto · payment",
  "auto.system": "Auto · system",
};
const typeLabel = (t: string) => TYPE_LABEL[t] ?? t;

type Stage = "" | "initial" | "final" | "between";
const STAGE_OPTIONS: { value: Stage; label: string }[] = [
  { value: "", label: "All" },
  { value: "initial", label: "Initial" },
  { value: "final", label: "Final" },
  { value: "between", label: "In between" },
];
const inStage = (s: Status, stage: Stage) =>
  stage === "" ||
  (stage === "initial" && s.is_initial) ||
  (stage === "final" && s.is_final) ||
  (stage === "between" && !s.is_initial && !s.is_final);

type SortKey = "order" | "label" | "code" | "type";
const SORTS: { value: SortKey; label: string }[] = [
  { value: "order", label: "Order" },
  { value: "label", label: "Label" },
  { value: "code", label: "Code" },
  { value: "type", label: "Type" },
];
const SORT_VALUE: Record<SortKey, (s: Status) => SortValue> = {
  order: (s) => s.display_order,
  label: (s) => s.label,
  code: (s) => s.code,
  type: (s) => typeLabel(s.transition_type),
};

// Label and order at every width; the initial / final flags from sm, the code from md and the type
// from lg (narrower screens show them under the label).
const COLUMNS: TableColumn[] = [
  { key: "code", header: "Code", width: "minmax(0,1fr)", from: "md" },
  { key: "label", header: "Label", width: "minmax(0,1.4fr)" },
  { key: "type", header: "Type", width: "8rem", from: "lg" },
  { key: "initial", header: "Initial", width: "4rem", from: "sm", align: "center" },
  { key: "final", header: "Final", width: "3.5rem", from: "sm", align: "center" },
  { key: "order", header: "Order", width: "3.5rem", align: "end" },
  { key: "actions", header: "Actions", width: "4rem", align: "end" },
];

const EXPORT_COLUMNS: ExportColumn<Status>[] = [
  { header: "Order", value: (s) => s.display_order },
  { header: "Code", value: (s) => s.code },
  { header: "Label", value: (s) => s.label },
  { header: "Description", value: (s) => s.description },
  { header: "Type", value: (s) => typeLabel(s.transition_type) },
  { header: "Initial", value: (s) => s.is_initial },
  { header: "Final", value: (s) => s.is_final },
  { header: "Shown to patients", value: (s) => s.patient_visible },
  { header: "Active", value: (s) => s.active },
];

export function StatusMaster() {
  const qc = useQueryClient();
  const { data: statuses = [], isLoading, isError, error, refetch } = useQuery({ queryKey: ["statuses"], queryFn: listStatuses });
  const [editing, setEditing] = useState<string | null>(null);
  const [label, setLabel] = useState("");

  // list controls
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [stage, setStage] = useState<Stage>("");
  const [sort, setSort] = useState<SortKey>("order");
  const [descending, setDescending] = useState(false);
  const [perPage, setPerPage] = useState(25);

  // The types the master uses, plus the one picked.
  const typeOptions = [
    { value: "", label: "All" },
    ...[...new Set([...statuses.map((s) => s.transition_type), type].filter(Boolean))]
      .map((t) => ({ value: t, label: typeLabel(t) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const matching = narrowAndSort(statuses, {
    q,
    searchText: (s) => `${s.code} ${s.label} ${s.description ?? ""}`,
    keep: (s) => (!type || s.transition_type === type) && inStage(s, stage),
    sortValue: SORT_VALUE[sort],
    descending,
  });
  const { page, total, hasMore, loadMore } = useClientPages(
    matching,
    perPage,
    JSON.stringify([q, type, stage, sort, descending, perPage]),
  );
  const narrowing = (type ? 1 : 0) + (stage ? 1 : 0);
  const activeFilters = narrowing + (q.trim() ? 1 : 0);

  const update = useMutation({
    mutationFn: (s: Status) => updateStatus(s.id, { label: label.trim() }),
    onSuccess: () => {
      setEditing(null);
      toast.success("Status saved.");
      qc.invalidateQueries({ queryKey: ["statuses"] });
    },
    onError: toastFailure, // ADM-13: a refused save showed nothing
  });

  return (
    <div className="flex h-full flex-col gap-3 sm:gap-4">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Status master</h2>
          <p className="text-sm text-gray-500">Code is immutable. Label, colour and order are editable.</p>
        </div>
        {/* Every status the search and filters match, in the table's order. */}
        <ExportButton table="statuses" columns={EXPORT_COLUMNS} rows={matching} disabled={isLoading} />
      </div>

      <FilterBar
        label="Search and filter statuses"
        narrowing={narrowing}
        search={<SearchBox value={q} onChange={setQ} placeholder="Search by code, label or description…" />}
      >
        <SelectFilter label="Type" value={type} options={typeOptions} onChange={setType} testId="status-filter-type" />
        <SelectFilter label="Stage" value={stage} options={STAGE_OPTIONS} onChange={setStage} testId="status-filter-stage" />
        <SortFilter
          value={sort}
          options={SORTS}
          descending={descending}
          onChange={(s) => {
            setSort(s);
            setDescending(false);
          }}
          onToggle={() => setDescending((d) => !d)}
        />
        <BatchSizeFilter value={perPage} onChange={setPerPage} />
        <ClearFilters
          count={activeFilters}
          onClear={() => {
            setQ("");
            setType("");
            setStage("");
          }}
        />
      </FilterBar>

      <VirtualTable
        label="Statuses"
        columns={COLUMNS}
        rows={page}
        rowKey={(s) => s.id}
        rowHeight={56}
        measureRows
        loading={isLoading}
        error={isError ? describeError(error) : null}
        onRetry={() => void refetch()}
        empty={activeFilters ? "No statuses match your filters." : "No statuses."}
        total={total}
        hasMore={hasMore}
        onLoadMore={loadMore}
        resetKey={JSON.stringify([q, type, stage, sort, descending])}
        renderRow={(s) => {
          const details = [
            <span key="code" className="font-mono md:hidden">{s.code}</span>,
            <span key="type" className="lg:hidden">{typeLabel(s.transition_type)}</span>,
            s.is_initial && <span key="initial" className="sm:hidden">initial</span>,
            s.is_final && <span key="final" className="sm:hidden">final</span>,
          ];
          return (
            <TableRow
              columns={COLUMNS}
              className="min-h-14 py-2 text-sm transition-colors hover:bg-gray-50"
              cells={{
                code: <span className="block truncate font-mono text-xs">{s.code}</span>,
                label: (
                  <div className="min-w-0">
                    {editing === s.id ? (
                      <input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        maxLength={60}
                        aria-label={`Label for ${s.code}`}
                        aria-invalid={!label.trim()}
                        className={`w-full min-w-0 rounded-sm border px-2 py-1 ${label.trim() ? "" : "border-red-400"}`}
                      />
                    ) : (
                      <div className="truncate">{s.label}</div>
                    )}
                    {/* What the columns left out at this width would show. */}
                    <div className="flex flex-wrap gap-x-2 text-xs text-gray-400 lg:hidden">{details}</div>
                  </div>
                ),
                type: <span className="block truncate text-gray-600">{typeLabel(s.transition_type)}</span>,
                initial: s.is_initial ? "✓" : "",
                final: s.is_final ? "✓" : "",
                order: <span className="tabular-nums">{s.display_order}</span>,
                actions: (
                  <RowActions>
                    {editing === s.id ? (
                      // Patients see the label: it can't be saved blank (ADM-14).
                      <IconButton
                        label={label.trim() ? "Save" : "Enter a label to save"}
                        tone="success"
                        disabled={!label.trim() || update.isPending}
                        onClick={() => update.mutate(s)}
                      >
                        <SaveIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        label="Edit"
                        tone="brand"
                        onClick={() => {
                          setEditing(s.id);
                          setLabel(s.label);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </RowActions>
                ),
              }}
            />
          );
        }}
      />
    </div>
  );
}
